import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import UserAvatar from '../common/UserAvatar';
import { Send, X, Image, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ChatPanel({ bookingId, otherUser, onClose, wsRef, onMessagesRead }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const userScrolledUpRef = useRef(false);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = (force = false, instant = false) => {
    // Only scroll if user hasn't scrolled up or if forced
    if (force || !userScrolledUpRef.current) {
      // Use direct scrollTop manipulation instead of scrollIntoView
      // This prevents smooth animation from interfering with user scrolls
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  // Handle scroll to detect if user scrolled up
  const handleScroll = (e) => {
    const container = e.target;
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    userScrolledUpRef.current = !isAtBottom;
  };

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/messages/${bookingId}`);
      const newMessages = res.data;
      
      setMessages(prev => {
        // Check if there are actually NEW messages (not just a re-fetch)
        const hasNewMessages = newMessages.length > prev.length;
        
        // Check if the new message is from someone else (not current user)
        const isFromOther = hasNewMessages && 
          newMessages[newMessages.length - 1]?.sender_id !== user?.id;
        
        // Only auto-scroll for new messages from others if user hasn't scrolled up
        // IMPORTANT: Check userScrolledUpRef INSIDE the callback to get the latest value
        if (hasNewMessages && !isInitialLoadRef.current && isFromOther) {
          // Delay and re-check if user is still scrolled up
          setTimeout(() => {
            if (!userScrolledUpRef.current) {
              scrollToBottom();
            }
          }, 100);
        }
        
        return newMessages;
      });
      
      // On initial load, scroll to bottom instantly (only on FIRST load)
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        setTimeout(() => scrollToBottom(true, true), 50);
      }
      
      // Notify parent that messages have been read
      if (onMessagesRead) {
        onMessagesRead();
      }
    } catch (e) {
      console.error('Failed to fetch messages:', e);
    }
  }, [bookingId, onMessagesRead, user?.id]);

  // Track if this is the very first mount
  const hasMountedRef = useRef(false);

  useEffect(() => {
    // Only set isInitialLoad on the FIRST mount, not on dependency changes
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      isInitialLoadRef.current = true;
    }
    
    fetchMessages();
    // Poll for new messages every 5 seconds as fallback for real-time
    const pollInterval = setInterval(fetchMessages, 5000);
    return () => clearInterval(pollInterval);
  }, [fetchMessages]);

  // Only scroll to bottom when user sends their own message
  useEffect(() => {
    const currentCount = messages.length;
    if (currentCount > prevMessageCountRef.current && prevMessageCountRef.current > 0) {
      // New message added - check if it's from current user
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.sender_id === user?.id) {
        scrollToBottom(true); // Force scroll for own messages
      }
    }
    prevMessageCountRef.current = currentCount;
  }, [messages, user?.id]);

  // Listen for incoming chat messages via WebSocket
  useEffect(() => {
    const handleWsMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'chat_message' && data.message?.booking_id === bookingId) {
          setMessages(prev => {
            if (prev.find(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
        }
        if (data.type === 'chat_message_sent' && data.message?.booking_id === bookingId) {
          setMessages(prev => {
            if (prev.find(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
        }
      } catch (e) {
        console.warn('ChatPanel: failed to parse websocket message', e);
      }
    };

    if (wsRef?.current) {
      wsRef.current.addEventListener('message', handleWsMessage);
      return () => wsRef.current?.removeEventListener('message', handleWsMessage);
    }
  }, [wsRef, bookingId]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      // Try WebSocket first
      if (wsRef?.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'chat_message',
          booking_id: bookingId,
          content: newMessage.trim()
        }));
      } else {
        const res = await axios.post(`${API}/chat/messages`, {
          booking_id: bookingId,
          content: newMessage.trim()
        });
        setMessages(prev => [...prev, res.data]);
      }
      setNewMessage('');
      inputRef.current?.focus();
    } catch (e) {
      console.error('Failed to send message:', e);
    } finally {
      setSending(false);
    }
  };

  // Compress image to max 600px width before uploading
  const compressImage = (file, maxWidth = 600) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = document.createElement('img');
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          
          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob((blob) => {
            if (blob) {
              // Create new file from blob
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          }, 'image/jpeg', 0.85); // 85% quality
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    // Validate file type first
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setUploading(true);
    try {
      // Compress image to max 600px width
      let processedFile = file;
      try {
        processedFile = await compressImage(file, 600);
      } catch (compressError) {
        console.warn('Image compression failed, using original:', compressError);
        // Continue with original file if compression fails
      }
      
      // Validate size after compression (should be much smaller now)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (processedFile.size > maxSize) {
        toast.error('Image is too large. Please select a smaller image.');
        return;
      }
      
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', processedFile);
      formData.append('booking_id', bookingId);
      
      // Upload image
      const uploadRes = await axios.post(`${API}/chat/upload-image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const imageUrl = uploadRes.data.url;
      
      // Send message with image
      const res = await axios.post(`${API}/chat/messages`, {
        booking_id: bookingId,
        content: imageUrl,
        message_type: 'image'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setMessages(prev => [...prev, res.data]);
      toast.success('Image sent!');
    } catch (e) {
      console.error('Failed to upload image:', e);
      toast.error('Failed to send image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Helper to get full image URL (handles relative paths)
  const getImageUrl = (url) => {
    if (!url) return '';
    // If already absolute URL, return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If relative path, prepend the base URL
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const renderMessage = (msg) => {
    const isMine = msg.sender_id === user?.id;
    
    if (msg.message_type === 'image') {
      const imageUrl = getImageUrl(msg.content);
      return (
        <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[75%] rounded-2xl overflow-hidden ${
            isMine
              ? 'bg-slate-900 rounded-br-sm'
              : 'bg-white border border-stone-200 rounded-bl-sm'
          }`}>
            <img 
              src={imageUrl} 
              alt="Shared image" 
              className="max-w-full h-auto select-none"
              style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'contain' }}
              draggable={false}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/placeholder-image.png';
                e.target.alt = 'Image failed to load';
              }}
            />
            <p className={`text-xs px-3 py-1 ${isMine ? 'text-slate-400' : 'text-slate-400'}`}>
              {formatTime(msg.created_at)}
            </p>
          </div>
        </div>
      );
    }
    
    return (
      <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
          isMine
            ? 'bg-slate-900 text-white rounded-br-sm'
            : 'bg-white text-slate-900 border border-stone-200 rounded-bl-sm'
        }`}>
          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
          <p className={`text-xs mt-1 ${isMine ? 'text-slate-400' : 'text-slate-400'}`}>
            {formatTime(msg.created_at)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="chat-panel">
      {/* Chat Header - Fixed at top */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white z-10">
        <div className="flex items-center gap-3">
          <UserAvatar 
            src={otherUser?.profile_image} 
            name={otherUser?.name} 
            role={otherUser?.role}
            size="sm"
          />
          <div>
            <p className="font-semibold text-slate-900 text-sm">{otherUser?.name || 'Chat'}</p>
            <p className="text-xs text-slate-500">Booking #{bookingId?.slice(0, 8).toUpperCase()}</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="chat-close-btn">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        )}
      </div>

      {/* Messages - Scrollable */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-stone-50 min-h-0" 
        data-testid="chat-messages"
      >
        {messages.length === 0 && (
          <div className="text-center py-8">
            <p className="text-slate-400 text-sm">No messages yet. Start the conversation!</p>
          </div>
        )}
        {messages.map((msg) => renderMessage(msg))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input - Fixed at bottom */}
      <div className="flex-shrink-0 px-3 py-3 border-t border-stone-200 bg-white z-10">
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="chat-file-input"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="chat-camera-input"
        />
        
        <div className="flex items-center gap-2">
          {/* Gallery button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-10 h-10 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors disabled:opacity-50"
            title="Upload from gallery"
            data-testid="chat-gallery-btn"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Image className="w-5 h-5" />}
          </button>
          
          {/* Camera button */}
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            className="w-10 h-10 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors disabled:opacity-50"
            title="Take a photo"
            data-testid="chat-camera-btn"
          >
            <Camera className="w-5 h-5" />
          </button>
          
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2.5 rounded-full border border-stone-300 text-sm focus:outline-none focus:border-amber-500 bg-stone-50"
            data-testid="chat-input"
          />
          <Button
            size="sm"
            className="rounded-full w-10 h-10 p-0 bg-slate-900 hover:bg-slate-800"
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
